import { useState } from 'react';

import { Button } from '@components/button';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';

import { Loading } from 'components/Loading';
import ShowMore from 'components/ShowMore';
import { Modal, ModalProps } from 'features/modals';
import { IApplication } from 'models/types';

type Props = ModalProps<{
  applicants: IApplication[];
}>;

export const BulkDeleteApplicantsModal = (props: Props) => {
  const { t } = useScopedTranslate(locale);
  const { applicants, closeModal } = props;
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit() {
    const job = applicants[0].job;
    setSubmitting(true);

    const success = await job.bulkDeleteApplicants(applicants);

    if (success) {
      closeModal();
    } else {
      setSubmitting(false);
    }
  }

  return (
    <Modal>
      <Modal.Header text={t('confirmDeletion')} />
      {submitting && <Loading />}
      {applicants.length === 0 && (
        <>
          <Text text={t('noApplicants')} />
          <Modal.Actions>
            <Button variant="secondary" onClick={closeModal} text={t('close')} />
          </Modal.Actions>
        </>
      )}
      {applicants.length > 0 && (
        <>
          <Stack space={3}>
            <Text text={t('confirm', { number: applicants.length })} />
            <Text font="bold16" color="red100" text={t('warning')} />
            <ShowMore
              max={applicants.length}
              count={applicants.length > 15 ? 10 : applicants.length}
              inc={1000}
              render={({ count }: { count: number }) => (
                <div>
                  {applicants.slice(0, count).map((a, i) => (
                    <Text key={i} font="bold16" color="purple100" text={a.name} />
                  ))}
                </div>
              )}
            />
          </Stack>
          <Modal.Actions>
            <Button
              isDisabled={submitting}
              variant="secondary"
              onClick={closeModal}
              text={t('cancel')}
            />
            <Button isDisabled={submitting} onClick={onSubmit} text={t('submit')} />
          </Modal.Actions>
        </>
      )}
    </Modal>
  );
};

const locale = {
  en: {
    confirmDeletion: 'Confirm deletion',
    close: 'Close',
    noApplicants: 'There are no applicants in the selection',
    confirm: 'Are you sure you wish to delete these {number} applications?',
    warning: 'This action can not be undone!',
    cancel: 'Cancel',
    submit: 'Submit',
  },
  is: {
    confirmDeletion: 'Staðfesta eyðingu',
    close: 'Loka',
    noApplicants: 'Það eru engar umsóknir í valinu',
    confirm: 'Ertu viss um að þú viljir eyða þessum {number} umsóknum?',
    warning: 'Þessi aðgerð er óafturkræf!',
    cancel: 'Hætta við',
    submit: 'Staðfesta',
  },
};
